"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Label } from "@baluu/ui/components/label";

type StarRatingInputProps = {
  value: string;
  onChange: (v: string) => void;
  className?: string;
};

function StarRatingInput({ value, onChange, className }: StarRatingInputProps) {
  const filled = Number(value) || 0;
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star === filled ? "" : String(star))}
          aria-label={`${star} star${star === 1 ? "" : "s"}`}
          className="p-0.5 text-muted-foreground hover:text-primary transition-colors"
        >
          <Star
            className={cn(
              "size-5",
              star <= filled ? "fill-primary text-primary" : "fill-none",
            )}
          />
        </button>
      ))}
    </div>
  );
}

type CurrencyInputProps = {
  value: string;
  onChange: (v: string) => void;
  currencySymbol?: string;
  className?: string;
};

function CurrencyInput({
  value,
  onChange,
  currencySymbol,
  className,
}: CurrencyInputProps) {
  const [raw, setRaw] = useState(value);
  useEffect(() => {
    setRaw(value);
  }, [value]);
  return (
    <div className={cn("relative", className)}>
      {currencySymbol && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {currencySymbol}
        </span>
      )}
      <Input
        inputMode="decimal"
        className={currencySymbol ? "pl-7" : undefined}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          const n = Number(raw);
          const next =
            raw.trim() === "" || !Number.isFinite(n) || n < 0
              ? ""
              : n.toFixed(2);
          setRaw(next);
          onChange(next);
        }}
      />
    </div>
  );
}

type DateTimeInputProps = {
  value: string;
  onChange: (v: string) => void;
  className?: string;
};

function DateTimeInput({ value, onChange, className }: DateTimeInputProps) {
  return (
    <Input
      type="datetime-local"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
    />
  );
}

type UrlInputProps = {
  value: string;
  onChange: (v: string) => void;
  className?: string;
};

function UrlInput({ value, onChange, className }: UrlInputProps) {
  return (
    <Input
      type="url"
      placeholder="https://"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
    />
  );
}

type MultiSelectCheckboxGroupProps = {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  className?: string;
};

function MultiSelectCheckboxGroup({
  options,
  value,
  onChange,
  className,
}: MultiSelectCheckboxGroupProps) {
  // Namespace ids per instance so several groups can render on one page
  // without htmlFor/id collisions.
  const instanceId = React.useId();
  return (
    <div className={cn("space-y-2", className)}>
      {options.map((option, index) => {
        const id = `${instanceId}-option-${index}`;
        const checked = value.includes(option);
        return (
          <div key={id} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={checked}
              onCheckedChange={(next) =>
                onChange(
                  next === true
                    ? [...value, option]
                    : value.filter((existing) => existing !== option),
                )
              }
            />
            <Label htmlFor={id} className="font-normal cursor-pointer">
              {option}
            </Label>
          </div>
        );
      })}
    </div>
  );
}

const ADDRESS_PART_PLACEHOLDERS = [
  "Address line 1",
  "Address line 2",
  "City",
  "Postal code",
  "Country",
] as const;

type AddressFieldInputProps = {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Flattens the five address parts into the one opaque string the API stores
 * per custom field. Trims each part and drops empty ones so a partially
 * filled form doesn't emit stray separators (`", , "`), and an untouched
 * field emits `""` — which is what lets `isCustomerFieldAnswered`'s generic
 * `(answer ?? "") !== ""` check correctly treat it as unanswered.
 *
 * Pulled out as its own function (rather than inlined in `updatePart` below)
 * so it's unit-testable without jsdom/testing-library — neither is available
 * in this package, and this is the one piece of `AddressFieldInput` that
 * doesn't need a DOM to verify.
 */
function joinAddressParts(parts: string[]): string {
  return parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * Five-part address entry that stores one comma-joined string.
 *
 * The API stores exactly one opaque string per custom field
 * (`CustomerFieldValue.value`), so an address has to flatten. Comma-joining is
 * the convention the dashboard's customer editor already writes and reads
 * (edit-customer-details-popup.tsx), which is what lets a value entered here
 * round-trip through that editor.
 *
 * Reconstruction is lossy in the same way that editor's is: a prefilled value
 * can't be re-split reliably, so it lands wholly in line 1 rather than being
 * guessed apart.
 *
 * `value` can change out from under this component after mount — the basket
 * checkout's known-customer prefill writes a saved address into the shared
 * answers map once the login/recognition flow resolves, which is after this
 * component has already mounted with `value=""`. A one-shot `useState`
 * initializer alone would miss that update (blank inputs despite an answered
 * field) and silently overwrite the saved value the moment the buyer edited
 * any single line. So `parts` resyncs from `value` in an effect — but only
 * when the change didn't come from this component's own `onChange` (tracked
 * via `lastEmittedRef`), for the same reason `NumberFieldInput` in
 * `customer-fields.tsx` guards its resync on focus: unconditionally syncing
 * on every `value` change would also fire on the round-trip after our own
 * `onChange`, which is a no-op here (the parent's next `value` is exactly
 * what we just emitted) but would be wrong to treat as an external prefill.
 */
function AddressFieldInput({ value, onChange }: AddressFieldInputProps) {
  const [parts, setParts] = React.useState<string[]>(() => {
    const initial = ["", "", "", "", ""];
    if (value) initial[0] = value;
    return initial;
  });
  // What this component itself last emitted via onChange. Used to tell an
  // externally-driven `value` change (prefill) apart from the harmless
  // round-trip of our own emission coming back down as a prop.
  const lastEmittedRef = React.useRef(value);

  React.useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const next = ["", "", "", "", ""];
    if (value) next[0] = value;
    setParts(next);
    lastEmittedRef.current = value;
  }, [value]);

  function updatePart(index: number, next: string) {
    const nextParts = parts.map((part, i) => (i === index ? next : part));
    setParts(nextParts);
    const joined = joinAddressParts(nextParts);
    lastEmittedRef.current = joined;
    onChange(joined);
  }

  return (
    <div className="space-y-2">
      {ADDRESS_PART_PLACEHOLDERS.map((placeholder, index) => (
        <Input
          key={placeholder}
          value={parts[index]}
          placeholder={placeholder}
          onChange={(e) => updatePart(index, e.target.value)}
        />
      ))}
    </div>
  );
}

export {
  StarRatingInput,
  CurrencyInput,
  DateTimeInput,
  UrlInput,
  MultiSelectCheckboxGroup,
  AddressFieldInput,
  joinAddressParts,
};
export type {
  StarRatingInputProps,
  CurrencyInputProps,
  DateTimeInputProps,
  UrlInputProps,
  MultiSelectCheckboxGroupProps,
  AddressFieldInputProps,
};
